/**
 * 分拣批次 mock 静态数据（转写自产品交互原型「分拣批次 交互原型.dc.html」的 LA hub 数据）。
 *
 * 原型以固定时刻 2026/09/19 14:32 为「现在」，这里把每条批次的创建时间换算为相对「现在」的分钟偏移，
 * 运行时以当前时间回推，保证列表始终是「最近」的批次、72 小时停更判定有意义。
 * 原型中以 LA 为本 Hub 的流向，换算为当前仓的 Hub 前缀；其他 Hub 流向取第一个非本 Hub 的 Hub。
 *
 * todo batch mock: 待后端提供 Query sortingBatches / sortingBatchDestinations，接入后删除本文件。
 */
import { BatchStatus, CreationMethod, SortingBatch } from '../types';
import { MOCK_SORT_CODES } from './sortCodes';

/** 各 Hub 下的配送仓（原型 whsTree） */
export const MOCK_WAREHOUSE_TREE: Record<string, string[]> = {
    LA: ['LADC', 'SAN', 'SFO', 'SEA', 'PHX', 'DEN', 'LAS', 'SAC', 'POT', 'TUS', 'RNO', 'LAFSN', 'LAMOD', 'LATOK', 'LABKF', 'LAMNF', 'LAPMS', 'DENCSP', 'POTEUG', 'SEASPN'],
    EWR: ['NJ', 'JFK', 'PHL', 'BOS', 'DCA', 'CT'],
    ATL: ['ATL', 'CTL', 'GSP', 'BNA', 'SAV', 'AUG', 'CHA'],
    MIA: ['MIADC', 'ORL', 'TPA', 'WPB', 'JAX', 'FTM', 'SRQ'],
    ORD: ['ORDC', 'CVG', 'CMH', 'IND', 'MIL'],
    DFW: ['DFDC', 'AUS', 'SAT', 'DFWLOV', 'DFWWAL', 'DFWKIL'],
    HOU: ['HOU'],
};

export const MOCK_HUBS = Object.keys(MOCK_WAREHOUSE_TREE);

/** 原型数据的本 Hub */
const PROTO_OWN_HUB = 'LA';

/** 操作人（原型） */
export const MOCK_OPERATORS = {
    hubManager: 'Susan Ma',
    whsManager: 'Jack Chen',
    manualCreators: ['li.wei', 'zhang.min', 'chen.jie'],
};

export const ownWarehousesOf = (hubPrefix: string): string[] => MOCK_WAREHOUSE_TREE[hubPrefix] || MOCK_WAREHOUSE_TREE[PROTO_OWN_HUB];

export const otherHubsOf = (hubPrefix: string): string[] => MOCK_HUBS.filter((h) => h !== hubPrefix);

/**
 * 当前 Hub 适用的分拣码样本：本 Hub 不在样本中时，把原型 LA 的码换成本 Hub 前缀。
 * todo batch mock: 待后端提供 Query sortingCodesByDestination。
 */
export const getMockSortCodes = (hubPrefix: string): string[] => {
    if (MOCK_WAREHOUSE_TREE[hubPrefix]) return MOCK_SORT_CODES;
    return MOCK_SORT_CODES.map((c) => (c.startsWith(`${PROTO_OWN_HUB}-`) ? `${hubPrefix}${c.slice(PROTO_OWN_HUB.length)}` : c));
};

interface SeedRow {
    id: number;
    /** 目的地：own:<WHS> 本 Hub 配送仓；other 其他 Hub；other:<idx> 其他 Hub 的第 idx 个配送仓 */
    dest: string;
    packedBags: number;
    createdBags?: number;
    parcels: number;
    status: BatchStatus;
    /** 相对「现在」的分钟数 */
    minutesAgo: number;
}

/** 原型 swiftx.batches（状态已按原型规则折叠为 5 态：发运中/到件中 → 分拣结束，已到件 → 已完成） */
const OUTBOUND_SEEDS: SeedRow[] = [
    { id: 7, dest: 'own:SAN', packedBags: 0, createdBags: 8, parcels: 0, status: 'SORTING', minutesAgo: 302 },
    { id: 1, dest: 'own:SAN', packedBags: 12, createdBags: 18, parcels: 386, status: 'SORTING', minutesAgo: 502 },
    { id: 2, dest: 'own:LADC', packedBags: 22, createdBags: 30, parcels: 741, status: 'SORTING', minutesAgo: 497 },
    { id: 9, dest: 'other:0', packedBags: 14, createdBags: 18, parcels: 412, status: 'SORTING', minutesAgo: 447 },
    { id: 10, dest: 'other', packedBags: 16, createdBags: 20, parcels: 533, status: 'SORTING', minutesAgo: 462 },
    { id: 3, dest: 'own:PHX', packedBags: 6, parcels: 512, status: 'SORTING_ENDED', minutesAgo: 532 },
    { id: 8, dest: 'own:LADC', packedBags: 64, createdBags: 64, parcels: 2356, status: 'SORTING_ENDED', minutesAgo: 952 },
    { id: 4, dest: 'own:SFO', packedBags: 9, parcels: 204, status: 'COMPLETED', minutesAgo: 1142 },
    { id: 5, dest: 'other', packedBags: 11, createdBags: 14, parcels: 318, status: 'SORTING_ENDED', minutesAgo: 1227 },
    { id: 6, dest: 'own:SEA', packedBags: 0, parcels: 0, status: 'CANCELLED', minutesAgo: 1250 },
    { id: 11, dest: 'own:SAN', packedBags: 15, createdBags: 15, parcels: 468, status: 'SORTING_ENDED', minutesAgo: 6132 },
    { id: 12, dest: 'other:0', packedBags: 20, createdBags: 22, parcels: 597, status: 'SORTING_ENDED', minutesAgo: 7102 },
];

interface InboundSeedRow extends SeedRow {
    /** 来源 Hub 序号（在非本 Hub 列表中的位置） */
    sourceIndex: number;
}

/** 原型 swiftx.inbound：其他仓创建、发往本站的批次 */
const INBOUND_SEEDS: InboundSeedRow[] = [
    { id: 101, sourceIndex: 0, dest: 'own', packedBags: 16, createdBags: 16, parcels: 604, status: 'SORTING', minutesAgo: 552 },
    { id: 102, sourceIndex: 1, dest: 'own:LADC', packedBags: 9, createdBags: 11, parcels: 287, status: 'SORTING', minutesAgo: 582 },
    { id: 103, sourceIndex: 3, dest: 'own', packedBags: 21, createdBags: 21, parcels: 912, status: 'SORTING_ENDED', minutesAgo: 982 },
    { id: 104, sourceIndex: 2, dest: 'own:SAN', packedBags: 7, createdBags: 7, parcels: 158, status: 'COMPLETED', minutesAgo: 1137 },
];

const pad2 = (n: number): string => String(n).padStart(2, '0');

export const formatBatchTimePart = (time: number): { date: string; hm: string } => {
    const d = new Date(time);
    return {
        date: `${d.getFullYear()}${pad2(d.getMonth() + 1)}${pad2(d.getDate())}`,
        hm: `${pad2(d.getHours())}${pad2(d.getMinutes())}`,
    };
};

const resolveSeedDestination = (dest: string, hubPrefix: string): string => {
    const other = otherHubsOf(hubPrefix)[0];
    if (dest === 'own') return hubPrefix;
    if (dest === 'other') return other;
    const [kind, value] = dest.split(':');
    if (kind === 'own') return `${hubPrefix}-${value}`;
    const whs = MOCK_WAREHOUSE_TREE[other] || [];
    return `${other}-${whs[Number(value)] || whs[0] || 'WHS'}`;
};

const ACTIVE: BatchStatus[] = ['CREATED', 'SORTING'];

/**
 * 原型创建方式规则：id 为偶数、且没有更早的同流向「已创建 / 分拣中」批次时，视为分拣机自动创建。
 * 分拣机创建者为机器编号，人工创建者为账号。
 */
const applyCreationMethod = (rows: SortingBatch[]): SortingBatch[] => rows.map((b) => {
    const hasOlderActive = rows.some((o) => o !== b && o.destination === b.destination && ACTIVE.includes(o.status) && o.createdAt < b.createdAt);
    const method: CreationMethod = b.id % 2 === 0 && !hasOlderActive ? 'SORTER' : 'MANUAL';
    const creator = method === 'SORTER'
        ? `SRT-${b.creationSite}-0${(b.id % 3) + 1}`
        : MOCK_OPERATORS.manualCreators[b.id % 3];
    return { ...b, creationMethod: method, creator };
});

const toBatch = (row: SeedRow, site: string, destination: string, now: number): SortingBatch => {
    const createdAt = now - row.minutesAgo * 60 * 1000;
    const { date, hm } = formatBatchTimePart(createdAt);
    return {
        id: row.id,
        batchNumber: `${site}_${date}_${hm}_${destination}`,
        destination,
        creationSite: site,
        status: row.status,
        creationMethod: 'MANUAL',
        creator: '',
        packedBags: row.packedBags,
        createdBags: row.createdBags ?? row.packedBags,
        parcels: row.parcels,
        createdAt,
    };
};

/** 发出批次种子（本仓创建） */
export const buildOutboundSeed = (hubPrefix: string, now: number): SortingBatch[] =>
    applyCreationMethod(OUTBOUND_SEEDS.map((row) => toBatch(row, hubPrefix, resolveSeedDestination(row.dest, hubPrefix), now)));

/** 接收批次种子（他仓创建、发往本站） */
export const buildInboundSeed = (hubPrefix: string, now: number): SortingBatch[] => {
    const sources = otherHubsOf(hubPrefix);
    return applyCreationMethod(INBOUND_SEEDS.map((row) => {
        const site = sources[row.sourceIndex] || sources[0];
        return toBatch(row, site, resolveSeedDestination(row.dest, hubPrefix), now);
    }));
};

/** 新建批次后的下一个 id 起点 */
export const MOCK_NEXT_BATCH_ID = 1000;
